import i18n from '@providers/i18n';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ToastType } from '@shared/ui/Toast/models/Toast.enum';

export enum NotificationActions {
  SIMPLE_MESSAGE = 'simpleMessage',
  CLIENT_SHARE_ZIP_READY = 'clientShareZipReady',
  CLIENT_SHARE_ZIP_EMPTY = 'clientShareZipEmpty',
  CLASS_DOCUMENT_ZIP_READY = 'classDocumentZipReady',
  CLASS_DOCUMENT_ZIP_FAILED = 'classDocumentZipFailed',
}

interface Action {
  id: string;
  action: NotificationActions;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: Record<string, any>;
}

interface Toast {
  id: string;
  title?: string;
  description?: string;
  type: ToastType;
}

interface GlobalAlertState {
  toasts: Toast[];
  actions: Action[];
  subscriptions: string[];
}

const initialState: GlobalAlertState = {
  toasts: [],
  actions: [],
  subscriptions: [],
};

const notificationSlice = createSlice({
  name: 'notificationSlice',
  initialState,
  reducers: {
    addToast: (
      state,
      action: PayloadAction<{ title?: string; message: string; type: ToastType }>,
    ) => {
      state.toasts.push({
        id: Date.now().toString(),
        title: action.payload.title,
        description: action.payload.message,
        type: action.payload.type,
      });
    },
    removeToast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter(toast => toast.id !== action.payload);
    },
    removeAction: (state, action: PayloadAction<string>) => {
      state.actions = state.actions.filter(a => a.id !== action.payload);
    },
    sendNotification: (
      state,
      action: PayloadAction<{
        action: NotificationActions;
        payload: string;
      }>,
    ) => {
      const newAction = {
        id: new Date().getTime().toString(),
        action: action.payload.action,
        data: JSON.parse(action.payload.payload),
      };
      state.actions.push(newAction);

      // Send out an alert to the user
      switch (action.payload.action) {
        case NotificationActions.SIMPLE_MESSAGE:
          state.toasts.push({
            id: new Date().getTime().toString(),
            title: newAction.data.title,
            description: newAction.data.message,
            type: 'custom',
          });
          break;

        case NotificationActions.CLIENT_SHARE_ZIP_READY:
          state.toasts.push({
            id: new Date().getTime().toString(),
            title: i18n.t('notifications.clientShareZipReady.title'),
            description: i18n.t('notifications.clientShareZipReady.message'),
            type: 'success',
          });
          break;

        case NotificationActions.CLIENT_SHARE_ZIP_EMPTY:
          state.toasts.push({
            id: new Date().getTime().toString(),
            title: i18n.t('notifications.clientShareZipEmpty.title'),
            description: i18n.t('notifications.clientShareZipEmpty.message'),
            type: 'warning',
          });
          break;

        case NotificationActions.CLASS_DOCUMENT_ZIP_READY:
          state.toasts.push({
            id: new Date().getTime().toString(),
            title: i18n.t('notifications.classDocumentZipReady.title'),
            description: i18n.t('notifications.classDocumentZipReady.message'),
            type: 'success',
          });
          break;

        case NotificationActions.CLASS_DOCUMENT_ZIP_FAILED:
          state.toasts.push({
            id: new Date().getTime().toString(),
            title: i18n.t('notifications.classDocumentZipFailed.title'),
            description: i18n.t('notifications.classDocumentZipFailed.message'),
            type: 'error',
          });
          break;
      }
    },
  },
});

export const { addToast, removeToast, removeAction, sendNotification } = notificationSlice.actions;
export default notificationSlice.reducer;
